/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FileAttachment.tsx - Componente para archivos adjuntos
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Componente para preview y gestión de archivos adjuntos
 * @version 1.0.0
 * @architecture WASP Frontend
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFile, faFilePdf, faFileWord, faFileExcel, faFileImage, faFileVideo, faFileAudio } from '@fortawesome/free-solid-svg-icons';

// Tipos
export interface AttachedFile {
  file: File;
  preview?: string;
  type: 'image' | 'document' | 'video' | 'audio' | 'other';
}

interface FileAttachmentProps {
  files: AttachedFile[];
  onRemove: (index: number) => void;
  compact?: boolean;
}

/**
 * Obtener icono según tipo de archivo
 */
const getFileIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  
  switch (ext) {
    case 'pdf':
      return faFilePdf;
    case 'doc':
    case 'docx':
      return faFileWord;
    case 'xls':
    case 'xlsx':
      return faFileExcel;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return faFileImage;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'webm':
      return faFileVideo;
    case 'mp3':
    case 'wav':
    case 'ogg':
    case 'm4a':
      return faFileAudio;
    default:
      return faFile;
  }
};

/**
 * Formatear tamaño de archivo
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Obtener color de badge según tipo de archivo
 */
const getTypeBadgeColor = (type: AttachedFile['type']): string => {
  switch (type) {
    case 'image':
      return 'success';
    case 'video':
      return 'danger';
    case 'audio':
      return 'info';
    case 'document':
      return 'warning';
    default:
      return 'secondary';
  }
};

/**
 * Componente FileAttachment
 * 
 * Muestra preview de archivos adjuntos con opción de eliminar
 * 
 * @example
 * ```tsx
 * <FileAttachment
 *   files={attachedFiles}
 *   onRemove={(index) => removeFile(index)}
 *   compact={false}
 * />
 * ```
 */
export const FileAttachment: React.FC<FileAttachmentProps> = ({
  files,
  onRemove,
  compact = false
}) => {
  if (files.length === 0) {
    return null;
  }

  return (
    <div
      className="file-attachments-container"
      style={{
        padding: compact ? '8px' : '12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '12px'
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        }}
      >
        {files.map((attachedFile, index) => (
          <Card
            key={index}
            className="file-attachment-item"
            style={{
              width: compact ? '120px' : '150px',
              position: 'relative',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              overflow: 'hidden',
              transition: 'all 0.2s ease'
            }}
          >
            {/* Botón eliminar */}
            <Button
              variant="danger"
              size="sm"
              onClick={() => onRemove(index)}
              style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                zIndex: 10,
                width: '24px',
                height: '24px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                fontSize: '0.7rem'
              }}
              title="Eliminar archivo"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>

            {/* Preview del archivo */}
            {attachedFile.type === 'image' && attachedFile.preview ? (
              <div
                style={{
                  width: '100%',
                  height: compact ? '80px' : '100px',
                  backgroundImage: `url(${attachedFile.preview})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: compact ? '80px' : '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#e9ecef',
                  fontSize: compact ? '2rem' : '2.5rem',
                  color: '#6c757d'
                }}
              >
                <FontAwesomeIcon icon={getFileIcon(attachedFile.file.name)} />
              </div>
            )}

            {/* Info del archivo */}
            <Card.Body
              style={{
                padding: compact ? '6px' : '8px',
                backgroundColor: '#fff'
              }}
            >
              {/* Tipo de archivo */}
              <Badge
                bg={getTypeBadgeColor(attachedFile.type)}
                style={{
                  fontSize: '0.65rem',
                  marginBottom: '4px'
                }}
              >
                {attachedFile.type.toUpperCase()}
              </Badge>

              {/* Nombre del archivo */}
              <div
                style={{
                  fontSize: compact ? '0.7rem' : '0.75rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: '2px'
                }}
                title={attachedFile.file.name}
              >
                {attachedFile.file.name}
              </div>

              {/* Tamaño del archivo */}
              <div
                style={{
                  fontSize: '0.65rem',
                  color: '#6c757d'
                }}
              >
                {formatFileSize(attachedFile.file.size)}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      <style>{`
        .file-attachment-item {
          animation: fileAttachmentFadeIn 0.3s ease-out;
        }

        @keyframes fileAttachmentFadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .file-attachment-item:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }

        .file-attachment-item .btn-danger {
          opacity: 0.8;
          transition: opacity 0.2s ease;
        }

        .file-attachment-item:hover .btn-danger {
          opacity: 1;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .file-attachments-container {
            padding: 8px !important;
          }

          .file-attachment-item {
            width: 100px !important;
          }

          .file-attachment-item .card-body {
            padding: 6px !important;
            font-size: 0.7rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FileAttachment;
